<template>
  <div class="incorrect-items-view">
    <div class="incorrect-items-view__table">
      <div class="incorrect-items-view__header">
        <span class="incorrect-items-view__col-header"
          >Incorrect/ Missing?</span
        >
        <span class="incorrect-items-view__col-header">{{
          type == VisitIncorrectItemType.Ingredient
            ? "Ingredient Name"
            : "Brand Name"
        }}</span>
        <span class="incorrect-items-view__col-header">{{
          type == VisitIncorrectItemType.Ingredient ? "Source" : "Label"
        }}</span>
        <span class="incorrect-items-view__col-header">{{
          type == VisitIncorrectItemType.Ingredient ? "Cert. Agency" : "D/P/M"
        }}</span>
        <span class="incorrect-items-view__col-header">{{
          type == VisitIncorrectItemType.Ingredient
            ? "RMC / Comments"
            : "Comments"
        }}</span>
      </div>
      <div
        v-if="type == VisitIncorrectItemType.Ingredient"
        class="incorrect-items-view__items"
      >
        <div
          class="incorrect-items-view__item ingredient"
          v-for="(item, i) in (model! as InspectionIncorrectIngredient[])"
          :key="i"
        >
          <div>
            <div class="incorrect-items-view__item-radio-wrapper">
              <RadioButton
                :name="`group-${type}-${i}`"
                :value="true"
                :checked="item.IsIncorrect == true"
                @change="(e) => handleChange(e, item)"
                label="I"
                :disabled="disableModification"
              />
              <RadioButton
                :name="`group-${type}-${i}`"
                :value="false"
                :checked="item.IsIncorrect == false"
                @change="(e) => handleChange(e, item)"
                label="M"
                :disabled="disableModification"
              />
            </div>
          </div>
          <input
            v-if="!disableModification"
            type="text"
            v-model="item.IngredientName"
          />
          <p v-else class="item-value">
            {{ item.IngredientName }}
          </p>
          <input
            v-if="!disableModification"
            type="text"
            v-model="item.Source"
          />
          <p v-else class="item-value">
            {{ item.Source }}
          </p>
          <input
            v-if="!disableModification"
            type="text"
            v-model="item.CertifiedAgency"
          />
          <p v-else class="item-value">
            {{ item.CertifiedAgency }}
          </p>
          <input
            v-if="!disableModification"
            type="text"
            v-model="item.Comments"
          />
          <p v-else class="item-value">
            {{ item.Comments }}
          </p>
        </div>
      </div>
      <div v-else class="incorrect-items-view__items">
        <div
          class="incorrect-items-view__item product"
          v-for="(item, i) in (model! as InspectionIncorrectProduct[])"
          :key="i"
        >
          <div>
            <div class="incorrect-items-view__item-radio-wrapper">
              <RadioButton
                :name="`group-${type}-${i}`"
                :value="true"
                :checked="item.IsIncorrect == true"
                @change="(e) => handleChange(e, item)"
                label="I"
                :disabled="disableModification"
              />
              <RadioButton
                :name="`group-${type}-${i}`"
                :value="false"
                :checked="item.IsIncorrect == false"
                @change="(e) => handleChange(e, item)"
                label="M"
                :disabled="disableModification"
              />
            </div>
          </div>
          <input
            v-if="!disableModification"
            type="text"
            v-model="item.BrandName"
          />
          <p v-else class="item-value">
            {{ item.BrandName }}
          </p>
          <input v-if="!disableModification" type="text" v-model="item.Label" />
          <p v-else class="item-value">
            {{ item.Label }}
          </p>
          <input v-if="!disableModification" type="text" v-model="item.Dpm" />
          <p v-else class="item-value">
            {{ item.Dpm }}
          </p>
          <input
            v-if="!disableModification"
            type="text"
            v-model="item.Comments"
          />
          <p v-else class="item-value">
            {{ item.Comments }}
          </p>
        </div>
      </div>
    </div>
    <div class="incorrect-items-view__actions">
      <Button
        v-if="model!.length < itemLimit && !disableModification"
        themeColor="primary"
        type="button"
        @click="addItem"
      >
        <font-awesome-icon icon="plus" />
        <span>Add</span>
      </Button>
      <Button
        v-if="model!.length > 1 && !disableModification"
        themeColor="error"
        type="button"
        @click="removeItem(model!.length - 1)"
      >
        <font-awesome-icon icon="minus" />
        <span>Remove</span>
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { watchEffect } from "vue";
import { VisitIncorrectItemType } from "@/Domain/VisitIncorrectItemType";
import type { RadioButtonChangeEvent } from "@progress/kendo-vue-inputs";
import RadioButton from "@/shared/ui/inputs/RadioButton.vue";
import { Button } from "@progress/kendo-vue-buttons";
import type { InspectionIncorrectItem } from "@/Domain/InspectionIncorrectItem";
import type { InspectionIncorrectProduct } from "@/Domain/InspectionIncorrectProduct";
import type { InspectionIncorrectIngredient } from "@/Domain/InspectionIncorrectIngredient";

const props = defineProps<{
  type: VisitIncorrectItemType;
  disableModification?: boolean;
  addDefaultItem?: boolean;
}>();

const model = defineModel<InspectionIncorrectItem[]>();
const itemLimit = 7;

const addItem = function () {
  if (props.type == VisitIncorrectItemType.Product) {
    model.value!.push({
      IsIncorrect: true,
      Comments: "",
      BrandName: "",
      Label: "",
      Dpm: "",
    } as InspectionIncorrectProduct);
  } else {
    model.value!.push({
      IsIncorrect: true,
      Comments: "",
      IngredientName: "",
      Source: "",
      CertifiedAgency: "",
    } as InspectionIncorrectIngredient);
  }
};

const removeItem = function (index: number) {
  if (model.value!.length == 1) return;
  model.value!.splice(index, 1);
};

const handleChange = function (
  e: RadioButtonChangeEvent,
  item: InspectionIncorrectItem
) {
  item.IsIncorrect = e.value;
};

if (model.value!.length === 0) {
  addItem();
}

watchEffect(() => {
  if (props.addDefaultItem && model.value!.length === 0) addItem();
});
</script>

<style lang="scss" scoped>
.incorrect-items-view {
  margin-top: 12px;

  &__table {
    display: flex;
    flex-direction: column;
    width: 100%;
    overflow-x: auto;
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 0.5rem 0;
    border-bottom: 1px solid #ccc;
    font-weight: bold;
    font-size: 15px;
    gap: 16px;
    min-width: 400px;
  }

  &__col-header {
    flex: 1;
    font-size: 14px;
    font-weight: normal;
  }

  &__items {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    padding: 0.5rem 0;
    border-bottom: 1px solid #ccc;
    min-width: 400px;

    & > .incorrect-items-view__item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      padding: 0.5rem 0;
      gap: 16px;

      .item-value {
        text-wrap: wrap;
        word-wrap: break-word;
      }

      > * {
        flex: 1;
        min-width: 0px;
      }
    }
  }

  &__item-radio-wrapper {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 8px;

    @include media-breakpoint-up(sm) {
      margin: 0 12px;
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    width: 100%;
    padding: 0.5rem 0;
    margin-top: 1rem;
    gap: 16px;
  }
}
</style>
